import { memo, useLayoutEffect, useRef, useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { classNames } from 'primereact/utils';
import { Button, Checkbox, FilterIcon, CloseIcon } from '../../atoms';

// Virtual scrolling hook
interface UseVirtualScrollOptions {
  itemCount: number;
  itemHeight: number;
  containerHeight: number;
  overscan?: number;
}

const useVirtualScroll = ({ itemCount, itemHeight, containerHeight, overscan = 3 }: UseVirtualScrollOptions) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  // Calculate visible range
  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
  const endIndex = Math.min(itemCount, Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan);

  // Generate virtual items
  const virtualItems = [];
  for (let i = startIndex; i < endIndex; i++) {
    virtualItems.push({
      index: i,
      offsetTop: i * itemHeight,
    });
  }

  const totalHeight = itemCount * itemHeight;

  return {
    virtualItems,
    totalHeight,
    scrollRef,
    onScroll,
  };
};

interface FilterPopoverProps {
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  onClear: () => void;
  onApply: () => void;
}

const FilterPopover: React.FC<FilterPopoverProps> = ({ options, value, onChange, onClear, onApply }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((opt) => opt.toLowerCase().includes(query));
  }, [options, searchQuery]);

  // Virtual scrolling configuration
  const ITEM_HEIGHT = 36; // gap-3 + checkbox height
  const { virtualItems, totalHeight, scrollRef, onScroll } = useVirtualScroll({
    itemCount: filteredOptions.length,
    itemHeight: ITEM_HEIGHT,
    containerHeight: 280, // approximate visible height after header
    overscan: 5,
  });

  // Check if all filtered options are selected
  const allSelected = useMemo(() => {
    return filteredOptions.length > 0 && filteredOptions.every((opt) => value.includes(opt));
  }, [filteredOptions, value]);

  const handleToggleAll = () => {
    if (allSelected) {
      // Deselect all filtered options
      onChange(value.filter((v) => !filteredOptions.includes(v)));
    } else {
      // Select all filtered options (merge with existing selections)
      const newValue = [...value];
      filteredOptions.forEach((opt) => {
        if (!newValue.includes(opt)) {
          newValue.push(opt);
        }
      });
      onChange(newValue);
    }
  };

  return (
    <div
      className="flex flex-col rounded-lg border bg-white"
      style={{
        width: '22.5rem',
        maxHeight: '25rem',
        border: '1px solid #E1E1E0',
        boxShadow: '4px 4px 25px rgba(0, 0, 0, 0.25)',
      }}
    >
      {/* Header with search */}
      <div className="border-fletchers-gray-110 flex flex-col gap-3 border-b px-3 py-3">
        <div className="relative">
          <i className="pi pi-search absolute top-1/2 left-[10px] -translate-y-1/2 text-[0.875rem] text-gray-400" />
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={classNames(
              'border-fletchers-gray-110 h-8 w-full rounded-[4px] border bg-white pr-8 pl-[35px] text-[0.875rem] leading-5 text-black',
              'placeholder:text-fletchers-gray-200 focus:outline-none',
            )}
          />
          {searchQuery && (
            <button
              type="button"
              className="absolute top-1/2 right-[10px] -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600 focus:outline-none"
              onClick={() => setSearchQuery('')}
            >
              <CloseIcon width={10} height={10} />
            </button>
          )}
        </div>
      </div>

      {/* Options list with virtual scrolling */}
      {filteredOptions.length === 0 ? (
        <div className="flex min-h-0 flex-1 flex-col px-3 py-3">
          <span className="text-fletchers-gray-175 text-[0.875rem]">No results found</span>
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-b-lg px-3 py-3"
          onScroll={onScroll}
        >
          {/* Virtual scroll container */}
          <div style={{ height: `${totalHeight}px`, position: 'relative' }}>
            {virtualItems.map((virtualItem) => {
              const opt = filteredOptions[virtualItem.index];
              // Check if this is a provider option (contains triple space separator)
              const parts = opt.split('   ');
              const isProvider = parts.length === 2;

              return (
                <label
                  key={opt}
                  className="absolute right-0 left-0 flex cursor-pointer items-center gap-4"
                  style={{ top: `${virtualItem.offsetTop}px`, height: `${ITEM_HEIGHT}px` }}
                  title={opt.replace('   ', ' ')}
                >
                  <Checkbox
                    type="checkbox"
                    checked={value.includes(opt)}
                    onChange={(e) => {
                      if (e.target.checked) onChange([...value, opt]);
                      else onChange(value.filter((v) => v !== opt));
                    }}
                  />
                  {isProvider ? (
                    <span className="overflow-hidden text-[0.875rem] leading-5 overflow-ellipsis whitespace-nowrap">
                      <span className="text-black">{parts[0]}</span>
                      <span className="text-fletchers-gray-250"> {parts[1]}</span>
                    </span>
                  ) : (
                    <span className="overflow-hidden text-[0.875rem] leading-5 overflow-ellipsis whitespace-nowrap text-black">
                      {opt}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer buttons */}
      <div className="border-fletchers-gray-110 flex items-center justify-between border-t px-3 py-3">
        <button
          type="button"
          onClick={handleToggleAll}
          className="text-fletchers-gray-200 hover:text-fletchers-black-900 cursor-pointer text-[0.875rem] font-semibold focus:outline-none"
        >
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
        <div className="flex gap-2">
          <Button
            outlined
            className="hover:!bg-fletchers-gray-50 !h-8 !px-4 !text-[0.875rem] !font-semibold"
            onClick={onClear}
          >
            Clear
          </Button>
          <Button className="!h-8 !px-4 !text-[0.875rem] !font-semibold" onClick={onApply}>
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
};

interface DataTableHeaderFilterProps {
  columnId: string;
  filterOptions: string[];
  currentFilterValue: string[];
  setFilterValue: (value: string[]) => void;
  filterAnchor: string | null;
  setFilterAnchor: (columnId: string | null) => void;
}

export const DataTableHeaderFilter: React.FC<DataTableHeaderFilterProps> = memo(
  ({ columnId, filterOptions, currentFilterValue, setFilterValue, filterAnchor, setFilterAnchor }) => {
    const [pendingFilters, setPendingFilters] = useState<Record<string, string[]>>({});
    const [popoverLeft, setPopoverLeft] = useState<number | null>(null);
    const [popoverTop, setPopoverTop] = useState<number | null>(null);

    const iconRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    // Position popover when opened
    useLayoutEffect(() => {
      if (filterAnchor === columnId && iconRef.current) {
        const iconRect = iconRef.current.getBoundingClientRect();
        const popoverWidth = 360; // width of FilterPopover
        const pagePadding = 24;
        let left = iconRect.left;
        const top = iconRect.bottom + window.scrollY;

        // Ensure popover stays inside the viewport - align to right edge if
        // it would overflow
        if (iconRect.left + popoverWidth + pagePadding > window.innerWidth) {
          left = window.innerWidth - popoverWidth - pagePadding;
        }

        setPopoverLeft(left);
        setPopoverTop(top);
      }
    }, [filterAnchor, columnId]);

    // Handle clicks outside the popover
    useEffect(() => {
      if (filterAnchor !== columnId) return;

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;

        // Check if click is outside both the popover and the filter icon
        if (
          popoverRef.current &&
          !popoverRef.current.contains(target) &&
          iconRef.current &&
          !iconRef.current.contains(target)
        ) {
          setFilterAnchor(null);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [filterAnchor, columnId, setFilterAnchor]);

    return (
      <div className="relative pl-2" ref={iconRef}>
        <FilterIcon
          className={classNames('h-[16px] w-[16px]', 'cursor-pointer text-[#D9D9D9]', {
            '[&>path]:fill-fletchers-green-800':
              (currentFilterValue && currentFilterValue.length) || filterAnchor === columnId,
          })}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            if (filterAnchor === columnId) {
              setFilterAnchor(null);
            } else {
              setFilterAnchor(columnId);
              setPendingFilters((filters) => ({
                ...filters,
                [columnId]: currentFilterValue || [],
              }));
            }
          }}
        />
        {filterAnchor === columnId &&
          popoverLeft !== null &&
          popoverTop !== null &&
          ReactDOM.createPortal(
            <div ref={popoverRef} style={{ position: 'absolute', top: popoverTop, left: popoverLeft, zIndex: 9999 }}>
              <FilterPopover
                options={filterOptions}
                value={pendingFilters[columnId] || []}
                onChange={(vals) => setPendingFilters((f) => ({ ...f, [columnId]: vals }))}
                onClear={() => {
                  setFilterValue([]);
                  setFilterAnchor(null);
                }}
                onApply={() => {
                  setFilterValue(pendingFilters[columnId] || []);
                  setFilterAnchor(null);
                }}
              />
            </div>,
            document.body,
          )}
      </div>
    );
  },
);
